import { lazy, type ComponentType } from 'react';

/**
 * Centralized Route Configuration
 *
 * All route paths are defined here for easy maintenance.
 * Change VITE_ROUTER_BASENAME in .env to change the base path.
 *
 * Examples:
 * - VITE_ROUTER_BASENAME=/ -> /login (separate host per environment)
 * - VITE_ROUTER_BASENAME=/admin -> /admin/login
 */

// Get basename from environment variable, default to `/` (root domain)
export const ROUTER_BASENAME = import.meta.env.VITE_ROUTER_BASENAME || '/';

/**
 * Route paths (relative to basename)
 */
export const ROUTE_PATHS = {
  // Public routes
  LOGIN: 'login',
  REGISTER: 'register',
  UNAUTHORIZED: 'unauthorized',

  // Protected routes
  ROOT: '',
  OVERVIEW: 'overview',
  DASHBOARD: 'dashboard',
  METRICS: 'metrics',
  API_KEYS: 'api-keys',
  ENDPOINTS: 'endpoints',
  USAGE_ANALYTICS: 'usage',
  USERS: 'users',
  PERMISSIONS: 'permissions',
  QUEUE: 'queue',
  JOBS: 'jobs',
  JOB_DETAILS: (id: string) => `jobs/${id}`,
  MONITORING: 'monitoring',
  LOGS: 'logs',
  AUDIT_LOGS: 'audit-logs',
  SALESFORCE_LOGS: 'salesforce-logs',
  CRON_JOBS: 'cron-jobs',
  ERRORS: 'errors',
  ERROR_DETAILS: (id: string) => `errors/${id}`,
  PERFORMANCE: 'performance',
  REPORTS: 'reports',
  SETTINGS: 'settings',
  PROFILE: 'profile',
  NOTIFICATIONS: 'notifications',
  SALESFORCE_RESPONSE: 'salesforce-response',
} as const;

// Lazy load pages
const OverviewPage = lazy(() => import('@/pages/overview'));
const DashboardPage = lazy(() => import('@/pages/dashboard'));
const MetricsPage = lazy(() => import('@/pages/metrics'));
const ApiKeysPage = lazy(() => import('@/pages/api-keys'));
const EndpointsPage = lazy(() => import('@/pages/endpoints'));
const UsageAnalyticsPage = lazy(() => import('@/pages/usage-analytics'));
const PermissionsPage = lazy(() => import('@/pages/permissions'));
const QueuePageSimple = lazy(() => import('@/pages/queue-simple'));
const JobsPage = lazy(() => import('@/pages/jobs'));
const MonitoringPage = lazy(() => import('@/pages/monitoring'));
const LogsPage = lazy(() => import('@/pages/logs'));
const AuditLogsPage = lazy(() => import('@/pages/audit-logs'));
const SalesforceLogsPage = lazy(() => import('@/pages/salesforce-logs'));
const SalesforceResponsePage = lazy(() => import('@/pages/salesforce-response'));
const CronJobsPage = lazy(() => import('@/pages/cron-jobs'));
const ErrorsPage = lazy(() => import('@/pages/errors'));
const PerformancePage = lazy(() => import('@/pages/performance'));
const ReportsPage = lazy(() => import('@/pages/reports'));
const SettingsPage = lazy(() => import('@/pages/settings'));
const ProfilePage = lazy(() => import('@/pages/profile'));
const NotificationsPage = lazy(() => import('@/pages/notifications'));
const LoginPage = lazy(() => import('@/pages/login'));
const RegisterPage = lazy(() => import('@/pages/register'));
const UnauthorizedPage = lazy(() => import('@/pages/unauthorized'));
const UsersPage = lazy(() => import('@/features/users').then(m => ({ default: m.UsersPage })));
const JobDetailsPage = lazy(() => import('@/pages/job-details'));
const ErrorDetailsPage = lazy(() => import('@/pages/error-details'));
const NotFoundPage = lazy(() => import('@/pages/not-found'));

export type RouteAccess = 'public' | 'protected' | 'admin' | 'superadmin';

export interface RouteDefinition {
  path: string;
  component: ComponentType;
  access: RouteAccess;
  title: string;
}

export const ROUTES: RouteDefinition[] = [
  { path: ROUTE_PATHS.LOGIN, component: LoginPage, access: 'public', title: 'Login' },
  { path: ROUTE_PATHS.REGISTER, component: RegisterPage, access: 'public', title: 'Register' },
  { path: ROUTE_PATHS.UNAUTHORIZED, component: UnauthorizedPage, access: 'public', title: 'Unauthorized' },

  { path: ROUTE_PATHS.OVERVIEW, component: OverviewPage, access: 'protected', title: 'Overview' },
  { path: ROUTE_PATHS.DASHBOARD, component: DashboardPage, access: 'protected', title: 'Dashboard' },
  { path: ROUTE_PATHS.METRICS, component: MetricsPage, access: 'protected', title: 'Key Metrics' },
  { path: ROUTE_PATHS.API_KEYS, component: ApiKeysPage, access: 'protected', title: 'API Keys' },
  { path: ROUTE_PATHS.ENDPOINTS, component: EndpointsPage, access: 'protected', title: 'Endpoints' },
  { path: ROUTE_PATHS.USAGE_ANALYTICS, component: UsageAnalyticsPage, access: 'protected', title: 'Usage Analytics' },
  { path: ROUTE_PATHS.PROFILE, component: ProfilePage, access: 'protected', title: 'Profile' },
  { path: ROUTE_PATHS.QUEUE, component: QueuePageSimple, access: 'protected', title: 'Queue Management' },
  { path: ROUTE_PATHS.JOBS, component: JobsPage, access: 'protected', title: 'Job Details' },
  { path: `${ROUTE_PATHS.JOBS}/:id`, component: JobDetailsPage, access: 'protected', title: 'Job Details' },
  { path: ROUTE_PATHS.MONITORING, component: MonitoringPage, access: 'protected', title: 'Real-time Monitor' },
  { path: ROUTE_PATHS.LOGS, component: LogsPage, access: 'protected', title: 'Live Logs' },
  { path: ROUTE_PATHS.AUDIT_LOGS, component: AuditLogsPage, access: 'protected', title: 'Audit Trail' },
  { path: ROUTE_PATHS.SALESFORCE_LOGS, component: SalesforceLogsPage, access: 'protected', title: 'Salesforce Logs' },
  {
    path: ROUTE_PATHS.SALESFORCE_RESPONSE,
    component: SalesforceResponsePage,
    access: 'protected',
    title: 'Salesforce Response',
  },
  { path: ROUTE_PATHS.CRON_JOBS, component: CronJobsPage, access: 'protected', title: 'Cron Jobs' },
  { path: ROUTE_PATHS.PERFORMANCE, component: PerformancePage, access: 'protected', title: 'Performance' },

  { path: ROUTE_PATHS.USERS, component: UsersPage, access: 'admin', title: 'Users' },
  { path: ROUTE_PATHS.PERMISSIONS, component: PermissionsPage, access: 'admin', title: 'Permissions' },
  { path: ROUTE_PATHS.REPORTS, component: ReportsPage, access: 'admin', title: 'Reports' },
  { path: ROUTE_PATHS.SETTINGS, component: SettingsPage, access: 'admin', title: 'Settings' },
  { path: ROUTE_PATHS.NOTIFICATIONS, component: NotificationsPage, access: 'admin', title: 'Notifications' },

  { path: ROUTE_PATHS.ERRORS, component: ErrorsPage, access: 'superadmin', title: 'Error Tracking' },
  { path: `${ROUTE_PATHS.ERRORS}/:id`, component: ErrorDetailsPage, access: 'superadmin', title: 'Error Tracking' },
];

export { NotFoundPage };

/**
 * Get full path with basename
 * @param path - Route path (relative to basename)
 * @returns Full path including basename
 */
export function getFullPath(path: string): string {
  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;

  // Handle empty basename
  if (!ROUTER_BASENAME || ROUTER_BASENAME === '/') {
    return `/${cleanPath}`;
  }

  // Ensure basename doesn't have trailing slash
  const cleanBasename = ROUTER_BASENAME.endsWith('/')
    ? ROUTER_BASENAME.slice(0, -1)
    : ROUTER_BASENAME;

  return `${cleanBasename}/${cleanPath}`;
}

/**
 * Get login URL (used for redirects)
 */
export function getLoginUrl(): string {
  return getFullPath(ROUTE_PATHS.LOGIN);
}

/**
 * Get unauthorized URL
 */
export function getUnauthorizedUrl(): string {
  return getFullPath(ROUTE_PATHS.UNAUTHORIZED);
}

/**
 * Check if current path is a public route (login/unauthorized)
 */
export function isPublicRoute(pathname: string): boolean {
  const loginPath = getFullPath(ROUTE_PATHS.LOGIN);
  const registerPath = getFullPath(ROUTE_PATHS.REGISTER);
  const unauthorizedPath = getFullPath(ROUTE_PATHS.UNAUTHORIZED);

  return (
    pathname === loginPath ||
    pathname === registerPath ||
    pathname === unauthorizedPath ||
    pathname.includes('/login') ||
    pathname.includes('/register') ||
    pathname.includes('/unauthorized')
  );
}

/**
 * Route to page title mapping
 * Uses full paths to handle basename correctly
 */
const ROUTE_TITLES_FROM_ROUTES = Object.fromEntries(
  ROUTES.filter(route => !route.path.includes(':')).map(route => [
    getFullPath(route.path),
    route.title,
  ]),
);

export const ROUTE_TITLES: Record<string, string> = {
  ...ROUTE_TITLES_FROM_ROUTES,
  [getFullPath(ROUTE_PATHS.ROOT)]: 'Overview',
  '*': 'Not Found',
};
